import styled, { css } from 'styled-components';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { cssVar, setCssVar } from '@helpers/cssVar';
import animationCurve from '@helpers/animation';
const TextLinkStyles = css `
  position: relative;

  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: 2px;
    width: 100%;
    height: 1px;
    background-color: ${cssVar('fgColor')};
    transition: transform 0.3s, background-color 0.3s;
    transition-timing-function: ${animationCurve.secondary};
    transform: scaleX(0);

    @media (prefers-reduced-motion: reduce) {
      transition: background-color 0.3s;
    }
  }
`;
const Text = styled.div `
  ${TextLinkStyles};
`;
const PrimaryStyles = css `
  ${setCssVar('bgColor', brand.sage.sageDark)};
  ${setCssVar('bgColorAlt', brand.sage.sageMedium)};
  ${setCssVar('fgColor', brand.white)};
  border: 2px solid ${brand.sage.sageDark};
  background-color: ${cssVar('bgColor')};
  color: ${cssVar('fgColor')};

  &:hover {
    background-color: ${cssVar('bgColorAlt')};
    border-color: ${cssVar('bgColorAlt')};
  }

  &:active {
    border-color: ${brand.black};
    background-color: ${cssVar('bgColor')};
  }
  &:focus {
    outline: none;
    border-color: ${brand.black};
    background-color: ${cssVar('bgColorAlt')};
  }
`;
const SecondaryStyles = css `
  ${setCssVar('bgColor', brand.white)};
  ${setCssVar('bgColorAlt', brand.grey.greyLight)};
  ${setCssVar('fgColor', brand.black)};
  background-color: ${cssVar('bgColor')};
  color: ${cssVar('fgColor')};
  border: 2px solid ${brand.white};

  &:hover {
    background-color: ${cssVar('bgColorAlt')};
    border-color: ${cssVar('bgColorAlt')};
  }

  &:active {
    border-color: ${brand.black};
    background-color: ${cssVar('bgColor')};
  }
  &:focus {
    outline: none;
    border-color: ${brand.black};
    background-color: ${cssVar('bgColorAlt')};
  }
`;
const TextOnlyStyles = css `
  ${setCssVar('fgColor', brand.black)};
  ${setCssVar('bgColor', brand.sage.sageLight)};
  border: none;
  border-radius: 1px;
  background: none;
  color: ${cssVar('fgColor')};
  padding: 0;

  @media ${from(Device.TabletLarge)} {
    padding: 0;
  }

  ${Text} {
    &:after {
      transform: scaleX(1);
    }
  }

  &:hover {
    color: ${cssVar('bgColor')};

    ${Text} {
      &:after {
        transform: scaleX(0.5);
        background-color: ${cssVar('bgColor')};
      }
    }
  }

  &:active {
    color: ${cssVar('fgColor')};
  }

  &:focus {
    color: ${cssVar('bgColor')};
    outline: 2px solid ${cssVar('bgColor')};
    outline-offset: 6px;

    ${Text} {
      &:after {
        background-color: ${cssVar('bgColor')};
      }
    }
  }
`;
const LargeStyles = css `
  padding: 24px 20px;

  .innerText {
    font-size: 24px;
    line-height: 30px;
  }
`;
const SmallStyles = css `
  padding: 4px 8px;

  ${Text} {
    font-size: 14px;
    line-height: 16px;
  }

  @media ${from(Device.DesktopSmall)} {
    padding: 6px 12px;

    ${Text} {
      font-size: 16px;
      line-height: 18px;
    }
  }
`;
const IconLeft = css `
  padding-right: 15px;

  @media ${until(Device.DesktopSmall)} {
    padding-right: 0;
  }
`;
const IconRight = css `
  padding-left: 15px;

  @media ${until(Device.DesktopSmall)} {
    padding-left: 0;
  }
`;
const hideText = css `
  ${Text} {
    @media ${until(Device.DesktopSmall)} {
      display: none;
    }
  }
`;
const Icon = styled.div `
  display: inline-block;
  vertical-align: middle;

  ${({ position }) => {
    switch (position) {
        case 'left':
            return IconLeft;
        case 'right':
            return IconRight;
        default:
            break;
    }
}}

  svg {
    max-height: 24px;
    width: 20px;
  }
`;
const Button = styled.button `
  display: inline-block;
  padding: 7px 35px 8px;
  border-radius: 100000px;
  transition: color 0.3s, background-color 0.3s, border-color 0.3s;
  transition-timing-function: ${animationCurve.secondary};
  user-select: none;

  &,
  &:hover {
    [data-bg-color='sage1'] &,
    [data-bg-color='sage3'] & {
      border-color: ${brand.white};

      [data-theme='${1 /* HomeStepper */}'] & {
        border-color: ${brand.homeStepper.white};
      }
    }
  }

  @media ${from(Device.DesktopSmall)} {
    padding: 11px 40px;
  }

  ${Text} {
    ${fonts.matter.medium};
    font-size: 16px;
    line-height: 20px;
    display: inline-block;
    vertical-align: middle;

    @media ${from(Device.DesktopSmall)} {
      font-size: 20px;
      line-height: 28px;
    }
  }

  &:focus {
    ${Text} {
      &:after {
        transform: scaleX(1);
      }
    }
  }

  ${({ branding }) => {
    switch (branding) {
        case 'textOnly':
            return TextOnlyStyles;
        case 'primary':
            return PrimaryStyles;
        case 'secondary':
            return SecondaryStyles;
        default:
            break;
    }
}}

  ${({ iconPosition }) => {
    switch (iconPosition) {
        case 'right':
            return hideText;
        default:
            break;
    }
}}

  ${({ size }) => {
    switch (size) {
        case 'large':
            return LargeStyles;
        case 'small':
            return SmallStyles;
        default:
            break;
    }
}}
`;
export const ButtonStyles = {
    Button,
    Icon,
    Text,
    TextLinkStyles,
};
